import React, { useState } from "react";
import { Button, Input, message, Upload } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { setUser, setLoading, setError } from "../../redux/userSlice";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { updateProfil } from "../login/AuthService";
import { Buffer } from "buffer";
import Page404 from "app/not-found";

const RegisterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  background-color: #f0f2f5;
`;

const FormWrapper = styled.div`
  width: 100%;
  max-width: 400px;
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const ErrorText = styled.div`
  color: red;
  margin-bottom: 8px;
`;

const RegisterTitle = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const UploadProfile = styled(Upload)`
  .ant-upload {
    width: 70px !important;
    height: 70px !important;
    border-radius: 50%;
    overflow: hidden;
  }
`;

const PageProfil = () => {
  const { user } = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(
    user?.img
      ? `data:${user.img.contentType};base64,${Buffer.from(
          user.img.data
        ).toString("base64")}`
      : null
  );

  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [password, setPassword] = useState(user.password);

  const registerSchema = Yup.object().shape({
    name: Yup.string().required("Username is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
  });

 const handleAvatarChange = (info) => {
  const file = info.file.originFileObj || info.file;
  if (!file) return;

  setAvatarFile(file);

  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.src = e.target.result;

    img.onload = () => {
      const displaySize = 70; // taille CSS
      const scale = window.devicePixelRatio || 1; // retina
      const canvasSize = displaySize * scale;

      const canvas = document.createElement("canvas");
      canvas.width = canvasSize;
      canvas.height = canvasSize;

      const ctx = canvas.getContext("2d");
      ctx.clearRect(0, 0, canvasSize, canvasSize);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      // Calculer ratio pour que l'image remplisse le carré sans déformation
      const ratio = Math.max(
        canvasSize / img.width,
        canvasSize / img.height
      );

      const newWidth = img.width * ratio;
      const newHeight = img.height * ratio;

      const dx = (canvasSize - newWidth) / 2;
      const dy = (canvasSize - newHeight) / 2;

      ctx.drawImage(img, dx, dy, newWidth, newHeight);

      // Data URL pour le preview
      setAvatarPreview(canvas.toDataURL("image/png"));
    };
  };
  reader.readAsDataURL(file);
};


  const handleRegister = async (values, { setSubmitting, setErrors }) => {
    const formData = new FormData();
    formData.append("userId", user._id);
    formData.append("name", name);
    formData.append("email", email);
    formData.append("password", password);
    if (avatarFile) formData.append("avatar", avatarFile);

    dispatch(setError(null));
    dispatch(setLoading(true));

    try {
      const response = await updateProfil(formData);

      dispatch(
        setUser({
          user: response,
          token: response.token,
        })
      );

      message.success("Sauvegarde réussie !");
      navigate("/admin");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Erreur lors de la sauvegarde";
      dispatch(setError(errorMessage));
      message.error(errorMessage);
      setErrors({ general: errorMessage });
    } finally {
      dispatch(setLoading(false));
      setSubmitting(false);
    }
  };

  if (!user) return <Page404 />;

  return (
    <RegisterContainer>
      <FormWrapper>
        <RegisterTitle>
          <h2>
            <b>Profil</b>
          </h2>
        </RegisterTitle>

        <Formik
          initialValues={{ name, email, password }}
          validationSchema={registerSchema}
          onSubmit={handleRegister}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form>
              <label>Nom d’usager</label>
              <Field
                as={Input}
                size="large"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              {touched.name && errors.name && (
                <ErrorText>{errors.name}</ErrorText>
              )}

              <label style={{ marginTop: 16 }}>Courriel</label>
              <Field
                as={Input}
                size="large"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {touched.email && errors.email && (
                <ErrorText>{errors.email}</ErrorText>
              )}

              <label style={{ marginTop: 16 }}>Mot de passe</label>
              <Field
                as={Input.Password}
                size="large"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              {touched.password && errors.password && (
                <ErrorText>{errors.password}</ErrorText>
              )}

              <div style={{ marginTop: 16 }}>
                <label>Avatar</label>
                <UploadProfile
                  listType="picture-circle"
                  showUploadList={false}
                  beforeUpload={() => true} // Important pour le preview
                  onChange={handleAvatarChange}
                >
                  {avatarPreview ? (
                    <img
                      src={avatarPreview}
                      alt="Avatar"
                      style={{
                        width: "70px",
                        height: "70px",
                        objectFit: "cover",
                        borderRadius: "50%",
                        imageRendering: "auto",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "70px",
                        height: "70px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "1px dashed #ccc",
                        borderRadius: "50%",
                      }}
                    >
                      <PlusOutlined />
                    </div>
                  )}
                </UploadProfile>
              </div>

              <Button
                type="primary"
                htmlType="submit"
                block
                size="large"
                loading={isSubmitting}
                style={{ marginTop: 24 }}
              >
                Sauvegarder
              </Button>
            </Form>
          )}
        </Formik>
      </FormWrapper>
    </RegisterContainer>
  );
};

export default PageProfil;
